import { Button, Loading, Typography, useAlert } from "matsuri-ui"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { useCallback, useMemo, useState } from "react"
import { Navigate, useNavigate, useParams } from "react-router-dom"
import { css } from "@emotion/react"
import CloseIcon from "@mui/icons-material/Close"
import { CompletedView } from "./CompletedView"
import { ConfirmView } from "./ConfirmView"
import { MaxQuantity, SelectItemsView } from "./SelectItemsView"
import { CenteredBody, Main } from "./styled"
import { Header } from "@/components/Header/Header"
import { MobileAppHeader } from "@/components/Header/MobileAppHeader"
import { BackToDetailConfirmDialog } from "@/components/Dialog/BackToDetailConfirmDialog"
import { assertIsDefined } from "@/utils/assetIsDefined"
import { genCleaningReportPagePath } from "@/routes/protected"
import { nextPathAfterRoomSupply } from "@/pages/CleaningReportPage/routes/nextPath"
import { useCleaning } from "@/hooks/useCleaning"
import { RoomSupplyReportResponse, useRoomSupply } from "@/hooks/useRoomSupply"
import { useMobileLayoutProps } from "@/hooks/useMobileLayoutProps"
import { useNavigateBackToDetail } from "@/hooks/useNavigateBackToDetail"
import { useTranslation } from "@/hooks/useTranslation"

export const RoomSupplyPage = () => {
    const { t } = useTranslation()
    const { cleaningId } = useParams<"cleaningId">()
    assertIsDefined(cleaningId)

    const navigate = useNavigate()
    const { token } = useAuth()
    const { throwAlert } = useAlert()

    const { data: cleaning } = useCleaning(token, cleaningId)
    const listingName = cleaning?.cleaningInfo.listingName

    const {
        isEnabled,
        items,
        existingReport,
        isLoading,
        error,
        completeReport
    } = useRoomSupply(cleaningId)

    // 0 の品目は報告に含めない。
    const [quantities, setQuantities] = useState<Record<string, number>>({})
    // 品目選択 → 内容の確認 → 送信完了 の3段階をこの画面の中で切り替える
    const [phase, setPhase] = useState<"select" | "confirm" | "done">("select")
    const [submitting, setSubmitting] = useState(false)
    const [report, setReport] = useState<RoomSupplyReportResponse>()

    const {
        confirmBackOpen,
        openConfirmBack,
        closeConfirmBack,
        goBackToDetail
    } = useNavigateBackToDetail("cleaning", cleaningId)

    // フェーズを跨いでヘッダー/フッターの高さを測り直さないよう、ここで1度だけ呼ぶ
    const layout = useMobileLayoutProps()

    const selectedItems = useMemo(
        () =>
            items
                .map(item => ({ item, quantity: quantities[item.id] ?? 0 }))
                .filter(({ quantity }) => quantity > 0),
        [items, quantities]
    )

    const handleQuantityChange = useCallback((itemId: string, diff: number) => {
        setQuantities(prev => {
            const next = Math.min(
                MaxQuantity,
                Math.max(0, (prev[itemId] ?? 0) + diff)
            )
            return { ...prev, [itemId]: next }
        })
    }, [])

    const goNext = useCallback(() => {
        navigate(
            genCleaningReportPagePath(cleaningId, nextPathAfterRoomSupply())
        )
    }, [navigate, cleaningId])

    const handleConfirmClick = useCallback(() => {
        setPhase("confirm")
    }, [])

    const handleBackToSelect = useCallback(() => {
        setPhase("select")
    }, [])

    const handleSubmit = useCallback(async () => {
        setSubmitting(true)
        try {
            const result = await completeReport(
                selectedItems.map(({ item, quantity }) => ({
                    itemId: item.id,
                    quantity
                }))
            )
            setReport(result)
            setPhase("done")
        } catch (e) {
            throwAlert(e instanceof Error ? e : new Error(String(e)), {
                errorMessage: {
                    happend: t("備品の不足報告に失敗しました"),
                    action: t(
                        "ネットワーク接続を確認してから、もう一度お試しください。問題が続く場合は、管理者にお問い合わせください。"
                    )
                },
                ignoreSuccess: true
            })
        } finally {
            setSubmitting(false)
        }
    }, [completeReport, selectedItems, throwAlert, t])

    const backToDetailDialog = (
        <BackToDetailConfirmDialog
            type="cleaning"
            open={confirmBackOpen}
            onClose={closeConfirmBack}
            onConfirm={goBackToDetail}
        />
    )

    // 対象判定の取得に失敗した場合。報告フローを止めないようスキップ導線を出す。
    if (error) {
        return (
            <Main style={layout.rootProps.style}>
                <Header {...layout.headerProps}>
                    <MobileAppHeader
                        title={
                            <Typography variant="h4">
                                {t("備品の不足確認")}
                            </Typography>
                        }
                        onNext={openConfirmBack}
                        nextIcon={<CloseIcon />}
                    />
                </Header>
                <CenteredBody>
                    <Typography color="error">
                        {t(
                            "備品情報の取得に失敗しました。電波の良い場所で開き直すか、スキップして報告を続けてください。"
                        )}
                    </Typography>
                    <Button variant="filled" color="primary" onClick={goNext}>
                        {t("スキップして次へ進む")}
                    </Button>
                </CenteredBody>
                {backToDetailDialog}
            </Main>
        )
    }

    if (isLoading || isEnabled === undefined) {
        return (
            <Main style={layout.rootProps.style}>
                <CenteredBody>
                    <Loading
                        css={css`
                            font-size: 28px;
                        `}
                    />
                </CenteredBody>
            </Main>
        )
    }

    // 備品チェック対象でない物件はこの画面をスキップしてゴミストッカー確認へ
    if (!isEnabled) {
        return (
            <Navigate
                to={genCleaningReportPagePath(
                    cleaningId,
                    nextPathAfterRoomSupply()
                )}
                replace
            />
        )
    }

    if (phase === "done" && report) {
        return (
            <>
                <CompletedView
                    report={report}
                    layout={layout}
                    onNext={goNext}
                />
                {backToDetailDialog}
            </>
        )
    }

    // 既に報告済みの場合は二重報告させず、報告済みであることを表示する。
    // （フローを戻って再度この画面を通ったケース）
    if (existingReport) {
        return (
            <>
                <CompletedView
                    report={existingReport}
                    layout={layout}
                    onNext={goNext}
                    alreadyReported
                />
                {backToDetailDialog}
            </>
        )
    }

    if (phase === "confirm") {
        return (
            <>
                <ConfirmView
                    selectedItems={selectedItems}
                    submitting={submitting}
                    layout={layout}
                    onSubmit={handleSubmit}
                    onBack={handleBackToSelect}
                    onClose={openConfirmBack}
                />
                {backToDetailDialog}
            </>
        )
    }

    return (
        <>
            <SelectItemsView
                items={items}
                quantities={quantities}
                selectedCount={selectedItems.length}
                listingName={listingName}
                layout={layout}
                onQuantityChange={handleQuantityChange}
                onConfirm={handleConfirmClick}
                onSkip={goNext}
                onClose={openConfirmBack}
            />
            {backToDetailDialog}
        </>
    )
}
